/* BOA Ideas — small, dependency-free behaviours for the custom sections.
   Every piece is a custom element, so it re-initialises cleanly when the
   Shopify theme editor re-renders a section. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Hero: rotating word + image stack, kept in sync */
  class BoaRotator extends HTMLElement {
    connectedCallback() {
      this.words = JSON.parse(this.dataset.words || '[]');
      this.word = this.querySelector('[data-word]');
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.i = 0;
      if (reduceMotion || this.words.length < 2) return;
      this.timer = setInterval(() => this.next(), Number(this.dataset.interval || 2500));
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() {
      this.i = (this.i + 1) % this.words.length;
      if (this.word) {
        const span = document.createElement('span');
        span.className = 'boa-rotator__word';
        span.textContent = this.words[this.i];
        this.word.replaceChildren(span);
      }
      const n = this.cards.length;
      this.cards.forEach((card, k) => { card.dataset.pos = String((k - this.i % n + n) % n); });
    }
  }
  customElements.get('boa-rotator') || customElements.define('boa-rotator', BoaRotator);

  /* Header: menu panel, "/" opens search, Esc closes, focus returns */
  class BoaHeader extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-panel]');
      this.openers = [...this.querySelectorAll('[data-open-panel]')];
      this.closer = this.panel?.querySelector('[data-close-panel]');
      this.search = this.panel?.querySelector('[data-panel-search]');
      // The panel lives on <body>: a sticky/blurred header would otherwise trap the fixed panel inside it after scrolling.
      if (this.panel && this.panel.parentElement !== document.body) document.body.appendChild(this.panel);
      this.onKey = (e) => {
        const typing = /INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName) || document.activeElement?.isContentEditable;
        if (e.key === '/' && !typing && this.search && this.panel?.hidden) { e.preventDefault(); this.open(true); }
        else if (e.key === 'Escape' && this.panel && !this.panel.hidden) this.close();
      };
      this.openers.forEach((b) => b.addEventListener('click', () => this.open(b.hasAttribute('data-focus-search'), b)));
      this.closer?.addEventListener('click', () => this.close());
      this.panel?.addEventListener('click', (e) => { if (e.target.closest('a[href]')) this.close(true); });
      document.addEventListener('keydown', this.onKey);
    }
    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.documentElement.classList.remove('boa-panel-open'); document.body.style.overflow = '';
      if (this.panel && this.panel.parentElement === document.body) this.panel.remove();
    }
    open(focusSearch, trigger) {
      if (!this.panel) return;
      this.lastTrigger = trigger || document.activeElement;
      this.panel.hidden = false;
      document.documentElement.classList.add('boa-panel-open');
      document.body.style.overflow = 'hidden';
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      (focusSearch ? this.search : this.closer)?.focus();
    }
    close(silent) {
      if (!this.panel) return;
      this.panel.hidden = true;
      document.documentElement.classList.remove('boa-panel-open');
      document.body.style.overflow = '';
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (!silent) this.lastTrigger?.focus?.();
    }
  }
  customElements.get('boa-header') || customElements.define('boa-header', BoaHeader);

  /* Header colors: switch to the "after first section" style once the first section has scrolled past */
  (() => {
    const run = () => {
      const hdr = document.querySelector('boa-header[data-scroll-style]');
      if (!hdr) return;
      const main = document.getElementById('MainContent') || document.querySelector('main');
      const first = main && main.querySelector('.shopify-section');
      const update = () => {
        const hh = hdr.getBoundingClientRect().height;
        const past = first ? first.getBoundingClientRect().bottom <= hh + 1 : window.scrollY > 40;
        hdr.classList.toggle('is-scrolled', past);
      };
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
    };
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
  })();

  /* Testimonials slider */
  class BoaSlider extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.count = this.querySelector('[data-count]');
      this.i = 0;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1));
      this.render();
    }
    go(d) { this.i = (this.i + d + this.slides.length) % this.slides.length; this.render(); }
    render() {
      this.slides.forEach((s, k) => { s.hidden = k !== this.i; });
      if (this.count) this.count.textContent = `${String(this.i + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
    }
  }
  customElements.get('boa-slider') || customElements.define('boa-slider', BoaSlider);

  /* Live local clock: <boa-clock data-tz="Asia/Jerusalem"> */
  class BoaClock extends HTMLElement {
    connectedCallback() {
      let fmt;
      try { fmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: this.dataset.tz || 'Asia/Jerusalem' }); }
      catch (e) { fmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }); }
      const tick = () => { this.textContent = fmt.format(new Date()); };
      tick();
      this.timer = setInterval(tick, 15000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.get('boa-clock') || customElements.define('boa-clock', BoaClock);
  /* Work grid filter: buttons [data-filter] show cards whose data-tags contain the value */
  class BoaFilterGrid extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[data-filter]')];
      if (!this.buttons.length) return;
      // add industries that only exist in other grids on the page
      const all = new Set([...document.querySelectorAll('boa-filter-grid [data-tags]')].map((c) => c.dataset.tags).filter(Boolean));
      const have = new Set(this.buttons.map((b) => b.dataset.filter));
      const wrap = this.buttons[0].parentElement;
      all.forEach((t) => { if (!have.has(t)) { const b = this.buttons[0].cloneNode(false); b.dataset.filter = t; b.textContent = t; b.setAttribute('aria-pressed', 'false'); wrap.appendChild(b); this.buttons.push(b); } });
      this.buttons.forEach((b) => b.addEventListener('click', () => this.apply(b)));
    }
    apply(btn) {
      const v = btn.dataset.filter;
      this.buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
      document.querySelectorAll('boa-filter-grid [data-tags]').forEach((c) => { c.hidden = v !== 'all' && !c.dataset.tags.split('|').includes(v); });
    }
  }
  customElements.get('boa-filter-grid') || customElements.define('boa-filter-grid', BoaFilterGrid);

  /* ---------------- Motion layer ---------------- */
  const motionOn = () => document.documentElement.classList.contains('boa-motion') && !reduceMotion;
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }) : null;

  function splitWords(el) {
    if (el.dataset.boaSplit === 'done') return;
    let i = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((n) => {
        if (n.nodeType === 3 && n.nodeValue.trim()) {
          const frag = document.createDocumentFragment();
          // keep runs of non-Hebrew words (e.g. "BOA Ideas", "Shopify Plus") together so mixed RTL/LTR text keeps its order
          const tokens = [];
          n.nodeValue.split(/(\s+)/).filter(Boolean).forEach((t) => {
            // trailing punctuation after a Latin word belongs to the sentence direction, not to the Latin run
            const m = /^(.*[A-Za-z0-9])([.,:;!?)\]]+)$/.exec(t);
            if (m && !/[\u0590-\u05FF]/.test(t)) { tokens.push(m[1]); tokens.push('\u200B' + m[2]); } else tokens.push(t);
          });
          const groups = [];
          tokens.forEach((t) => {
            const isSpace = /^\s+$/.test(t);
            const ltr = !isSpace && /[A-Za-z0-9]/.test(t) && !/[\u0590-\u05FF\u0600-\u06FF]/.test(t);
            const last = groups[groups.length - 1];
            if (isSpace) { groups.push({ space: true, text: t }); return; }
            if (t.charAt(0) === '\u200B') { groups.push({ punct: true, text: t.slice(1) }); return; }
            if (ltr && last && last.space && groups[groups.length - 2] && groups[groups.length - 2].ltr) {
              groups.pop(); groups[groups.length - 1].text += last.text + t; return;
            }
            groups.push({ ltr, text: t });
          });
          groups.forEach((g) => {
            if (g.space) { frag.appendChild(document.createTextNode(g.text)); return; }
            if (g.punct) { const lastWord = frag.lastChild; if (lastWord && lastWord.nodeType === 1) { const pu = document.createElement('span'); pu.className = 'boa-punct'; pu.textContent = g.text; lastWord.after ? frag.appendChild(pu) : frag.appendChild(pu); } else frag.appendChild(document.createTextNode(g.text)); return; }
            const w = document.createElement('span'); w.className = 'boa-word';
            if (g.ltr) w.setAttribute('dir', 'ltr');
            const inner = document.createElement('span'); inner.style.setProperty('--i', i++); inner.textContent = g.text;
            w.appendChild(inner); frag.appendChild(w);
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1 && !n.classList.contains('boa-word') && !/^(BR|SVG|IMG)$/i.test(n.tagName)) { walk(n); }
      });
    };
    walk(el);
    el.dataset.boaSplit = 'done';
  }

  function countUp(el, dur) {
    const raw = el.textContent.trim();
    const m = raw.match(/^([^\d\[]*)([\d,.]+)(.*)$/);
    if (!m || raw.includes('[')) return;
    const target = parseFloat(m[2].replace(/,/g, ''));
    if (!isFinite(target) || target === 0) return;
    const decimals = (m[2].split('.')[1] || '').length;
    const fmt = (v) => m[1] + v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + m[3];
    const start = performance.now(); dur = dur || 1400;
    const tick = (t) => { const k = Math.min(1, (t - start) / dur); const e = 1 - Math.pow(1 - k, 3); el.textContent = fmt(target * e); if (k < 1) requestAnimationFrame(tick); };
    el.textContent = fmt(0); requestAnimationFrame(tick);
  }

  function initMotion(root) {
    if (!motionOn() || !io) return;
    const scope = root || document;
    scope.querySelectorAll('.boa h1, .boa h2.boa-h2, .boa .boa-display, .boa-tile__title, .boa-stackcard__title').forEach((el) => {
      if (el.closest('.boa-panel')) return;
      splitWords(el); el.setAttribute('data-boa-split', ''); el.setAttribute('data-boa-reveal', ''); io.observe(el);
    });
    const groups = '.boa-tile, .boa-work__card, .boa-card, .boa-step, .boa-feat, .boa-svc__card, .boa-partner, .boa-stats > div, .boa-stats3 > div, .boa-hscroll__item, .boa-acc details, .boa-lead';
    scope.querySelectorAll(groups).forEach((el) => {
      if (el.hasAttribute('data-boa-reveal') || el.closest('.boa-panel, .boa-stackcard')) return;
      const sibs = el.parentElement ? [...el.parentElement.children] : [];
      el.style.setProperty('--boa-d', `${Math.min(sibs.indexOf(el), 6) * 70}ms`);
      el.setAttribute('data-boa-reveal', ''); io.observe(el);
    });
    scope.querySelectorAll('.boa-page-hero__media .boa-media, .boa-work__card .boa-media, .boa-next .boa-media, .boa-gallery .boa-media').forEach((el) => {
      el.setAttribute('data-boa-reveal', ''); io.observe(el);
    });
    const countIO = new IntersectionObserver((entries) => entries.forEach((e) => { if (e.isIntersecting) { countUp(e.target); countIO.unobserve(e.target); } }), { threshold: 0.6 });
    scope.querySelectorAll('.boa-stats strong, .boa-stats3 strong, .boa-tile__big').forEach((el) => countIO.observe(el));
  }
  const boot = () => initMotion(document);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot();
  document.addEventListener('shopify:section:load', (e) => initMotion(e.target));

  /* Stacking cards: earlier cards shrink slightly as the next one arrives */
  class BoaStackCards extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('.boa-stackcard')];
      if (reduceMotion || this.cards.length < 2) return;
      this.onScroll = () => {
        this.cards.forEach((c, i) => {
          const next = this.cards[i + 1]; if (!next) return;
          const gap = next.getBoundingClientRect().top - c.getBoundingClientRect().top;
          const k = Math.max(0, Math.min(1, 1 - gap / c.offsetHeight));
          c.style.setProperty('--boa-s', String(1 - k * 0.06));
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true }); this.onScroll();
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
  }
  customElements.get('boa-stack-cards') || customElements.define('boa-stack-cards', BoaStackCards);

  /* Hover list: floating image follows the pointer */
  class BoaHoverList extends HTMLElement {
    connectedCallback() {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      this.box = this.querySelector('.boa-cursor-img');
      this.img = this.box?.querySelector('img');
      if (!this.box) return;
      this.querySelectorAll('[data-img]').forEach((row) => {
        row.addEventListener('mouseenter', () => { if (row.dataset.img) { this.img.src = row.dataset.img; this.box.classList.add('is-on'); } });
        row.addEventListener('mouseleave', () => this.box.classList.remove('is-on'));
      });
      this.onMove = (e) => { this.box.style.setProperty('--x', e.clientX + 'px'); this.box.style.setProperty('--y', e.clientY + 'px'); };
      this.addEventListener('mousemove', this.onMove);
    }
  }
  customElements.get('boa-hover-list') || customElements.define('boa-hover-list', BoaHoverList);

  /* Horizontal scroll: vertical scroll drives the track sideways (desktop) */
  class BoaHScroll extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.boa-hscroll__track');
      this.bar = this.querySelector('.boa-hscroll__progress');
      this.mq = window.matchMedia('(min-width: 750px)');
      this.rtl = getComputedStyle(this).direction === 'rtl';
      this.layout = () => {
        if (!this.mq.matches || reduceMotion) { this.style.height = ''; this.track.style.transform = ''; return; }
        this.dist = Math.max(0, this.track.scrollWidth - window.innerWidth);
        this.style.height = `${window.innerHeight + this.dist}px`;
        this.update();
      };
      this.update = () => {
        if (!this.mq.matches || reduceMotion) return;
        const r = this.getBoundingClientRect();
        const p = Math.max(0, Math.min(1, -r.top / (this.dist || 1)));
        this.track.style.transform = `translate3d(${(this.rtl ? 1 : -1) * p * this.dist}px,0,0)`;
        this.bar?.style.setProperty('--p', p);
      };
      window.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.layout);
      this.layout();
      window.addEventListener('load', this.layout, { once: true });
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.update); window.removeEventListener('resize', this.layout); }
  }
  customElements.get('boa-hscroll') || customElements.define('boa-hscroll', BoaHScroll);

  /* Video reel: play/pause control, pause off-screen, grow to full width on scroll */
  class BoaVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.btn = this.querySelector('[data-toggle]');
      this.userPaused = false;
      if (this.video) {
        this.video.muted = true; this.video.playsInline = true;
        if (this.dataset.label) this.video.setAttribute('aria-label', this.dataset.label);
        if (reduceMotion) { this.video.removeAttribute('autoplay'); this.video.pause(); this.setPressed(true); this.userPaused = true; }
        this.btn?.addEventListener('click', () => {
          if (this.video.paused) { this.video.play(); this.userPaused = false; this.setPressed(false); }
          else { this.video.pause(); this.userPaused = true; this.setPressed(true); }
        });
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(([e]) => {
            if (this.userPaused) return;
            e.isIntersecting ? this.video.play().catch(() => {}) : this.video.pause();
          }, { threshold: 0.15 });
          this.io.observe(this);
        }
      }
      if (this.dataset.grow === 'true' && !reduceMotion) {
        this.onScroll = () => {
          const r = this.getBoundingClientRect(), vh = window.innerHeight;
          const p = Math.max(0, Math.min(1, (vh - r.top) / (vh * 0.85)));
          const gutter = parseFloat(getComputedStyle(this.parentElement).getPropertyValue('--boa-gutter')) || 40;
          this.style.setProperty('--boa-reel-inset', `${(1 - p) * gutter}px`);
          this.style.setProperty('--boa-reel-radius', `${(1 - p) * 24}px`);
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.onScroll();
      }
    }
    setPressed(paused) {
      if (!this.btn) return;
      this.btn.setAttribute('aria-pressed', String(paused));
      this.btn.setAttribute('aria-label', paused ? (this.dataset.play || 'Play video') : (this.dataset.pause || 'Pause video'));
    }
    disconnectedCallback() { this.io?.disconnect(); window.removeEventListener('scroll', this.onScroll); window.removeEventListener('resize', this.onScroll); }
  }
  customElements.get('boa-video') || customElements.define('boa-video', BoaVideo);

  /* Counters: count up when visible */
  class BoaCounter extends HTMLElement {
    connectedCallback() {
      if (this.dataset.animate !== 'true' || reduceMotion || !('IntersectionObserver' in window)) return;
      const dur = Number(this.dataset.duration) || 1600;
      const io2 = new IntersectionObserver((entries) => entries.forEach((e) => {
        if (!e.isIntersecting) return;
        this.querySelectorAll('[data-count]').forEach((el) => countUp(el, dur));
        io2.disconnect();
      }), { threshold: 0.4 });
      io2.observe(this);
    }
  }
  customElements.get('boa-counter') || customElements.define('boa-counter', BoaCounter);

  /* Floating cards: pinned stage, per-card scroll movement, rotating words + animated bars */
  class BoaFloating extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('.boa-fc__card')];
      this.pin = this.dataset.pin === 'true';
      this.mq = window.matchMedia('(min-width: 750px)');
      this.manim = this.dataset.manim === 'true';
      this.isDesk = () => this.mq.matches || this.manim;
      this.kf = () => (this.mq.matches ? Math.min(1, window.innerWidth / 1440, window.innerHeight / 860) : Math.min(1, window.innerWidth / 760));
      this.words = [...this.querySelectorAll('[data-words]')];
      if (!reduceMotion) {
        this.onScroll = () => {
          if (!this.isDesk()) { this.cards.forEach((c) => ['--py','--px','--pa','--ps'].forEach((v) => c.style.removeProperty(v))); return; }
          const r = this.getBoundingClientRect(), vh = window.innerHeight;
          const k = this.kf();
          this.style.setProperty('--fc-k', k.toFixed(3));
          if (this.pin && this.dataset.mode === 'arrive') {
            // 0 = cards away (where "movement" points), 1 = resting place; starts as the section enters, ends with the pin
            const span = Math.max(1, r.height - vh) + vh * 0.55;
            const p = Math.max(0, Math.min(1, (vh * 0.55 - r.top) / span));
            this.cards.forEach((c) => {
              const d = (Number(c.dataset.delay) || 0) / 100;
              const lp = Math.max(0, Math.min(1, (p - d) / Math.max(0.05, 1 - d)));
              const e = 1 - Math.pow(1 - lp, 3);
              c.style.setProperty('--py', `${((Number(c.dataset.move) || 0) * k * (1 - e)).toFixed(1)}px`);
              c.style.setProperty('--px', `${((Number(c.dataset.moveX) || 0) * k * (1 - e)).toFixed(1)}px`);
              c.style.setProperty('--pa', Math.min(1, lp * 2.2).toFixed(2));
              c.style.setProperty('--ps', (0.86 + 0.14 * e).toFixed(3));
            });
            this.classList.toggle('is-scrolling', p > 0.04);
          } else if (this.pin) {
            // 0 when the stage reaches the top, 1 when the pin ends
            const span = Math.max(1, r.height - vh);
            const p = Math.max(0, Math.min(1.4, -r.top / span));
            this.cards.forEach((c) => {
              c.style.setProperty('--py', `${(Number(c.dataset.move) || 0) * p}px`);
              c.style.setProperty('--px', `${(Number(c.dataset.moveX) || 0) * p}px`);
            });
          } else if (this.dataset.parallax === 'true') {
            const center = r.top + r.height / 2 - vh / 2;
            this.cards.forEach((c) => c.style.setProperty('--py', `${(-center * (Number(c.dataset.depth) || 0) / 400).toFixed(1)}px`));
          }
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.onScroll();
      }
      // logo cloud: measure each logo, then place chips in the free space around the heading
      this.chips = [...this.querySelectorAll('[data-cloud]')];
      if (this.chips.length) {
        const load = (src) => new Promise((res) => { const im = new Image(); im.onload = () => res(im.naturalWidth / Math.max(1, im.naturalHeight)); im.onerror = () => res(3); im.src = src; });
        Promise.all(this.chips.map((c) => load(c.dataset.src))).then((ars) => {
          this.chips.forEach((c, i) => { c.dataset.ar = ars[i]; });
          this.packCloud();
          let t; this.onPackResize = () => { clearTimeout(t); t = setTimeout(() => this.packCloud(), 200); };
          window.addEventListener('resize', this.onPackResize);
          if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.packCloud());
        });
      }
      if (reduceMotion) this.querySelectorAll('.boa-fc__vid video').forEach((v) => { v.removeAttribute('autoplay'); v.pause(); });
      // a heading line set to "no break" shrinks to fit instead of being cut off
      this.fit = () => {
        const sec = this.closest('.boa-fc');
        if (!sec || !sec.classList.contains('boa-fc--l1-nowrap')) return;
        sec.querySelectorAll('.boa-fc__l1').forEach((el) => {
          el.style.fontSize = '';
          const cw = el.clientWidth, sw = el.scrollWidth;
          if (cw && sw > cw + 1) el.style.fontSize = `${(parseFloat(getComputedStyle(el).fontSize) * cw / sw * 0.97).toFixed(1)}px`;
        });
      };
      this.fit();
      window.addEventListener('resize', this.fit);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { this.fit(); if (this.avoid) this.avoid(); });
      this.placeCtas = () => {
        const ctas = this.querySelector('.boa-fc__ctas'), stage = this.querySelector('.boa-fc__stage'), title = this.querySelector('.boa-fc__title');
        if (!ctas || !stage || !title || !this.isDesk()) { if (ctas) ctas.style.top = ''; return; }
        const sr = stage.getBoundingClientRect(), tr = title.getBoundingClientRect();
        ctas.style.top = `${Math.round(tr.bottom - sr.top + 22)}px`;
      };
      // keep every card clear of the heading at its resting place (any screen size)
      this.avoid = () => {
        this.placeCtas();
        const stage = this.querySelector('.boa-fc__stage'), title = this.querySelector('.boa-fc__title');
        if (!stage || !title) return;
        if (!this.isDesk()) { this.cards.forEach((c) => c.style.removeProperty('--sy')); return; }
        const sr = stage.getBoundingClientRect();
        const spans = [...title.children].flatMap((el) => { const rg = document.createRange(); rg.selectNodeContents(el); return [...rg.getClientRects()]; }).concat([...this.querySelectorAll('.boa-fc__ctas .boa-btn')].map((b) => b.getBoundingClientRect()));
        if (!spans.length) return;
        const t = { l: Math.min(...spans.map((r) => r.left)) - sr.left - 14, r: Math.max(...spans.map((r) => r.right)) - sr.left + 14,
                    tp: Math.min(...spans.map((r) => r.top)) - sr.top - 14, b: Math.max(...spans.map((r) => r.bottom)) - sr.top + 14 };
        const tc = (t.tp + t.b) / 2;
        this.cards.filter((c) => !c.hasAttribute('data-cloud')).forEach((c) => {
          const w = c.offsetWidth, h = c.offsetHeight;
          const cx = (parseFloat(c.style.getPropertyValue('--x')) || 50) / 100 * sr.width, cy = (parseFloat(c.style.getPropertyValue('--y')) || 50) / 100 * stage.offsetHeight;
          const l = cx - w / 2, r = cx + w / 2, tp = cy - h / 2, b = cy + h / 2;
          let sy = 0;
          if (l < t.r && r > t.l && tp < t.b && b > t.tp) sy = cy < tc ? t.tp - b : t.b - tp;
          c.style.setProperty('--sy', `${Math.round(sy)}px`);
        });
      };
      this.avoid();
      window.addEventListener('resize', this.avoid);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.avoid);
      // rotating words (horizontal slide) + bars re-shuffle in the same card
      const lonely = [...this.querySelectorAll('.boa-fc__card')].filter((c) => c.querySelector('.boa-fc__bars') && !c.querySelector('[data-words]'));
      this.barTimers = reduceMotion ? [] : lonely.map((c) => setInterval(() => c.querySelectorAll('.boa-fc__bars i:not(.is-accent)').forEach((bar) => bar.style.setProperty('--bh', (0.25 + Math.random() * 0.55).toFixed(2))), 2600));
      this.timers = this.words.map((el) => {
        const list = el.dataset.words.split(',').map((w) => w.trim()).filter(Boolean);
        const card = el.closest('.boa-fc__card');
        const bars = card ? [...card.querySelectorAll('.boa-fc__bars i')] : [];
        if (list.length < 2 || reduceMotion) return null;
        let i = 0;
        return setInterval(() => {
          i = (i + 1) % list.length;
          const cur = el.querySelector('.boa-fc__word');
          cur.classList.add('is-out');
          setTimeout(() => {
            const span = document.createElement('span');
            span.className = 'boa-fc__word is-in'; span.textContent = list[i];
            el.replaceChildren(span);
          }, 380);
          bars.forEach((bar, k) => { if (!bar.classList.contains('is-accent')) bar.style.setProperty('--bh', (0.25 + Math.random() * 0.55).toFixed(2)); });
        }, 2600);
      });
    }
    packCloud() {
      const stage = this.querySelector('.boa-fc__stage'), title = this.querySelector('.boa-fc__title');
      if (!stage || !title) return;
      if (!this.isDesk()) { this.chips.forEach((c) => c.classList.add('is-placed')); return; }
      let seed = 7; const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
      const sr = stage.getBoundingClientRect(), W = sr.width, H = stage.offsetHeight;
      const k = this.kf();
      const hdr = document.querySelector('.boa-header');
      const top = Math.max(16, (hdr ? hdr.getBoundingClientRect().height : 0) + 8);
      const gap = 8 * k + 2;
      this.placeCtas && this.placeCtas();
      const spans = [...title.children].flatMap((el) => { const rg = document.createRange(); rg.selectNodeContents(el); return [...rg.getClientRects()]; }).concat([...this.querySelectorAll('.boa-fc__ctas .boa-btn')].map((b) => b.getBoundingClientRect()));
      const obstacles = spans.map((r) => [r.left - sr.left - 18, r.top - sr.top - 18, r.right - sr.left + 18, r.bottom - sr.top + 18]);
      this.cards.filter((c) => !c.hasAttribute('data-cloud')).forEach((c) => {
        const cx = (parseFloat(c.style.getPropertyValue('--x')) || 50) / 100 * W, cy = (parseFloat(c.style.getPropertyValue('--y')) || 50) / 100 * H;
        const w = c.offsetWidth, h = c.offsetHeight; obstacles.push([cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2]);
      });
      const placed = [], placedSrc = [], step = Math.max(8, Math.round(12 * k));
      const centersOf = () => placed.map((r) => [(r[0] + r[2]) / 2, (r[1] + r[3]) / 2]).concat(obstacles.slice(spans.length).map((r) => [(r[0] + r[2]) / 2, (r[1] + r[3]) / 2]));
      const order = this.chips.map((c, i) => i).sort((a, b) => (Number(this.chips[b].dataset.ar) - Number(this.chips[a].dataset.ar)) || (rnd() - 0.5));
      order.forEach((i) => {
        const c = this.chips[i];
        const h = (parseFloat(c.style.getPropertyValue('--h')) || 48) * k;
        const w = Math.max(h * 1.5, Math.min(h * 3.6, (h * 0.6 * Number(c.dataset.ar)) / 0.76));
        const free = (x0, y0, x1, y1) => x0 >= 8 && x1 <= W - 8 && y0 >= top && y1 <= H - 8 &&
          !placed.concat(obstacles).some((r) => x0 < r[2] + gap && x1 > r[0] - gap && y0 < r[3] + gap && y1 > r[1] - gap);
        let best = null; const cs = centersOf();
        for (let y = top + h / 2; y <= H - 8 - h / 2; y += step) {
          for (let x = 8 + w / 2; x <= W - 8 - w / 2; x += step) {
            if (!free(x - w / 2, y - h / 2, x + w / 2, y + h / 2)) continue;
            if (placedSrc.some(([src, a, b]) => src === c.dataset.src && Math.hypot(x - a, y - b) < W * 0.32)) continue;
            let d = Infinity; for (const [a, b] of cs) { const dd = (x - a) * (x - a) + (y - b) * (y - b); if (dd < d) d = dd; }
            d += rnd() * 400;
            if (!best || d > best[0]) best = [d, x, y];
          }
        }
        if (!best) { c.classList.remove('is-placed'); c.style.display = 'none'; return; }
        const [, x, y] = best; placed.push([x - w / 2, y - h / 2, x + w / 2, y + h / 2]); placedSrc.push([c.dataset.src, x, y]);
        const dx = x - W / 2, dy = y - H / 2, n = Math.hypot(dx, dy) || 1;
        c.style.display = '';
        c.style.setProperty('--x', (x / W * 100).toFixed(2)); c.style.setProperty('--y', (y / H * 100).toFixed(2));
        c.style.setProperty('--w', `${(w / k).toFixed(0)}px`);
        c.style.setProperty('--r', `${[-4, -3, -2, 2, 3, 4][Math.floor(rnd() * 6)]}deg`);
        c.dataset.move = Math.round(dy / n * 240); c.dataset.moveX = Math.round(dx / n * 260);
        c.dataset.delay = 10 + Math.floor(rnd() * 8) * 5;
        c.classList.add('is-placed');
      });
      if (this.avoid) this.avoid();
      if (this.onScroll) this.onScroll();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll); window.removeEventListener('resize', this.onScroll); window.removeEventListener('resize', this.avoid); window.removeEventListener('resize', this.onPackResize); window.removeEventListener('resize', this.fit);
      (this.timers || []).concat(this.barTimers || []).forEach((t) => t && clearInterval(t));
    }
  }
  customElements.get('boa-floating') || customElements.define('boa-floating', BoaFloating);

  /* Text direction "automatic per text": each text element picks its own direction */
  const DIR_TEXT = 'h1,h2,h3,h4,p,li,blockquote,figcaption,summary,dt,dd,label,.boa-display,.boa-eyebrow,.boa-btn,.boa-link,.boa-tile__title,.boa-tile__big,.boa-hoverlist__name,.boa-next__title,.boa-fc__t,.boa-fc__text,.boa-counters__label,.boa-stack__label,.boa-chip';
  function initDirAuto(root) {
    (root || document).querySelectorAll('[data-boa-dir-auto]').forEach((sec) => sec.querySelectorAll(DIR_TEXT).forEach((el) => el.setAttribute('dir', 'auto')));
    if (root && root.matches && root.matches('[data-boa-dir-auto]')) root.querySelectorAll(DIR_TEXT).forEach((el) => el.setAttribute('dir', 'auto'));
  }
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => initDirAuto()) : initDirAuto();
  document.addEventListener('shopify:section:load', (e) => initDirAuto(e.target));

  /* Metallic shine: turn *word* into <span class="boa-shine">word</span> inside BOA texts */
  function initShine(root) {
    const scope = root || document;
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, { acceptNode: (n) => (/\*[^*\n]+\*/.test(n.nodeValue) && n.parentElement && n.parentElement.closest('.boa') && !n.parentElement.closest('script,style,textarea,input,.boa-shine')) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT });
    const nodes = []; while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((n) => {
      const frag = document.createDocumentFragment();
      n.nodeValue.split(/(\*[^*\n]+\*)/).forEach((part) => {
        if (/^\*[^*\n]+\*$/.test(part)) { const sp = document.createElement('span'); sp.className = 'boa-shine'; sp.textContent = part.slice(1, -1); frag.appendChild(sp); }
        else if (part) frag.appendChild(document.createTextNode(part));
      });
      n.replaceWith(frag);
    });
  }
  initShine();
  document.addEventListener('shopify:section:load', (e) => initShine(e.target));

  /* Scroll progress line + contact bar that appears after the first screen */
  (() => {
    const run = () => {
      const bar = document.querySelector('.boa-progress span');
      const cta = document.querySelector('[data-boa-ctabar]');
      const tick = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const y = window.scrollY;
        if (bar) bar.parentElement.style.setProperty('--boa-progress', max > 0 ? (y / max).toFixed(4) : 0);
        if (cta) cta.classList.toggle('is-on', y > window.innerHeight * 0.6);
      };
      tick(); window.addEventListener('scroll', tick, { passive: true }); window.addEventListener('resize', tick);
    };
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
  })();
})();
